import { MasterPreset } from "./masterPresets";
import { Step } from "../components/sequencer/types";

/**
 * Formats a Step object as TypeScript code
 */
const formatStep = (step: Step, indent: string): string => {
  const lines: string[] = [];
  lines.push(`${indent}{`);
  lines.push(`${indent}  active: ${step.active},`);
  lines.push(`${indent}  mode: "${step.mode}",`);

  if (step.fxMixes && Object.keys(step.fxMixes).length > 0) {
    lines.push(`${indent}  fxMixes: {`);
    if (step.fxMixes.filter !== undefined) {
      lines.push(`${indent}    filter: ${step.fxMixes.filter},`);
    }
    if (step.fxMixes.reverb !== undefined) {
      lines.push(`${indent}    reverb: ${step.fxMixes.reverb},`);
    }
    if (step.fxMixes.delay !== undefined) {
      lines.push(`${indent}    delay: ${step.fxMixes.delay},`);
    }
    lines.push(`${indent}  },`);
  }

  lines.push(`${indent}}`);
  return lines.join("\n");
};

/**
 * Formats a MasterPreset as TypeScript code that can be pasted into masterPresets.ts
 * @param preset The preset to format
 * @returns A formatted string ready to be copied and pasted
 */
export const formatPresetAsCode = (preset: MasterPreset): string => {
  const lines: string[] = [];

  lines.push(`  ${preset.id}: {`);
  lines.push(`    id: "${preset.id}",`);
  lines.push(`    name: "${preset.name}",`);
  lines.push(`    description: "${preset.description}",`);
  lines.push(`    category: "${preset.category}",`);

  // Sample
  lines.push(`    sample: {`);
  lines.push(`      id: "${preset.sample.id}",`);
  lines.push(`    },`);

  // Pattern
  lines.push(`    pattern: {`);
  lines.push(`      preset: "${preset.pattern.preset}",`);
  lines.push(`    },`);

  // Chord
  lines.push(`    chord: {`);
  lines.push(`      key: "${preset.chord.key}",`);
  lines.push(`      progression: "${preset.chord.progression}",`);
  if (preset.chord.roman && preset.chord.roman.length > 0) {
    const roman = preset.chord.roman
      .map((chord) => `"${chord}"`)
      .join(", ");
    lines.push(`      roman: [${roman}],`);
    if (preset.chord.mode) {
      lines.push(`      mode: "${preset.chord.mode}",`);
    }
  } else if (preset.chord.customRoman && preset.chord.customRoman.length > 0) {
    const customRoman = preset.chord.customRoman
      .map((chord) => `"${chord}"`)
      .join(", ");
    lines.push(`      customRoman: [${customRoman}],`);
    if (preset.chord.customMode) {
      lines.push(`      customMode: "${preset.chord.customMode}",`);
    }
  }
  lines.push(`    },`);

  // Effects
  lines.push(`    effects: {`);
  lines.push(`      attack: ${preset.effects.attack},`);
  lines.push(`      decay: ${preset.effects.decay},`);
  lines.push(`      sustain: ${preset.effects.sustain},`);
  lines.push(`      release: ${preset.effects.release},`);
  if (preset.effects.adsrEnabled !== undefined) {
    lines.push(`      adsrEnabled: ${preset.effects.adsrEnabled},`);
  }
  lines.push(`      filterCutoff: ${preset.effects.filterCutoff},`);
  lines.push(`      filterType: "${preset.effects.filterType || "lowpass"}",`);
  lines.push(`      reverbMix: ${preset.effects.reverbMix},`);
  lines.push(`      reverbDecay: ${preset.effects.reverbDecay},`);
  lines.push(`      delayTime: "${preset.effects.delayTime}",`);
  lines.push(`      delayFeedback: ${preset.effects.delayFeedback},`);
  lines.push(`      delayMix: ${preset.effects.delayMix},`);
  if (preset.effects.reverseMix !== undefined) {
    lines.push(`      reverseMix: ${preset.effects.reverseMix},`);
  }
  lines.push(`    },`);

  // Sequencer
  lines.push(`    sequencer: {`);
  lines.push(`      bpm: ${preset.sequencer.bpm},`);
  lines.push(`      mode: "${preset.sequencer.mode}",`);
  lines.push(`    },`);

  // Steps count
  lines.push(`    stepsCount: ${preset.stepsCount},`);

  // Step pattern
  if (preset.stepPattern.length === 0) {
    lines.push(`    stepPattern: [],`);
  } else {
    lines.push(`    stepPattern: [`);
    preset.stepPattern.forEach((step, index) => {
      const formatted = formatStep(step, "      ");
      if (index < preset.stepPattern.length - 1) {
        lines.push(formatted + ",");
      } else {
        lines.push(formatted);
      }
    });
    lines.push(`    ],`);
    // Add a comment showing how many steps have FX mappings
    const stepsWithFX = preset.stepPattern.filter(
      (step) => step.fxMixes && Object.keys(step.fxMixes).length > 0
    ).length;
    if (stepsWithFX > 0) {
      lines.push(`    // ${stepsWithFX} step(s) have FX mappings`);
    }
  }

  // Register
  lines.push(
    `    register: { bottomNote: "${preset.register.bottomNote}", topNote: "${preset.register.topNote}" },`
  );

  // Ribbon positions
  if (preset.ribbonPositions.length === 0) {
    lines.push(`    ribbonPositions: [],`);
  } else {
    lines.push(`    ribbonPositions: [`);
    preset.ribbonPositions.forEach((pos, index) => {
      const comma = index < preset.ribbonPositions.length - 1 ? "," : "";
      lines.push(
        `      { startPercent: ${pos.startPercent}, endPercent: ${pos.endPercent} }${comma}`
      );
    });
    lines.push(`    ],`);
  }

  // FX modes
  lines.push(`    fxModes: {`);
  lines.push(`      filter: ${preset.fxModes.filter},`);
  lines.push(`      reverb: ${preset.fxModes.reverb},`);
  lines.push(`      delay: ${preset.fxModes.delay},`);
  lines.push(`      reverse: ${preset.fxModes.reverse},`);
  lines.push(`    },`);

  // Drone mode (optional)
  if (preset.droneMode) {
    lines.push(`    droneMode: "${preset.droneMode}",`);
  }

  // Drone notes (optional)
  if (preset.droneNotes && preset.droneNotes.length > 0) {
    const notesStr = preset.droneNotes.map((n) => `"${n}"`).join(", ");
    lines.push(`    droneNotes: [${notesStr}],`);
  }

  // Strum
  lines.push(`    strum: ${preset.strum},`);
  lines.push(`    groove: ${preset.groove},`);

  lines.push(`  },`);

  return lines.join("\n");
};
